import { useTranslation } from '../../../shared/i18n';
import React, { useState } from 'react';
import CountryFlag from '../../../shared/components/CountryFlag';
import {
  Card,
  Tabs,
  Form,
  Input,
  Select,
  Button,
  Space,
  message,
  Typography,
  Alert,
  Divider,
  Row,
  Col,
  Descriptions,
  Tag,
  Collapse
} from 'antd';
import {
  KeyOutlined,
  SafetyCertificateOutlined,
  LinkOutlined,
  CopyOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import apiClient, { RateLimitError } from '../../../shared/services/apiClient';
import InlineCaptcha from '../../../shared/components/InlineCaptcha';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const SslTools = () => {
  const { t } = useTranslation();
  // CSR Generator State
  const [csrForm] = Form.useForm();
  const csrCountry = Form.useWatch('country', csrForm);
  const [csrLoading, setCSRLoading] = useState(false);
  const [csrResult, setCsrResult] = useState(null);

  // Certificate Chain State
  const [chainForm] = Form.useForm();
  const [chainLoading, setChainLoading] = useState(false);
  const [chainResult, setChainResult] = useState(null);

  // Certificate Decoder State
  const [decoderForm] = Form.useForm();
  const [decoderLoading, setDecoderLoading] = useState(false);
  const [decoderResult, setDecoderResult] = useState(null);

  // Key Matcher State
  const [matcherForm] = Form.useForm();
  const [matcherLoading, setMatcherLoading] = useState(false);
  const [matcherResult, setMatcherResult] = useState(null);

  // Copy to clipboard
  const copyToClipboard = (text, name) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => message.success(t('{{name}} copied to clipboard', { name })))
        .catch(() => message.error(t('Failed to copy')));
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(t('{{name}} copied to clipboard', { name }));
      } catch (err) {
        message.error(t('Failed to copy'));
      }
      document.body.removeChild(textArea);
    }
  };

  // Download file
  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Generate CSR
  const generateCSR = async (values) => {
    setCSRLoading(true);
    try {
      const response = await apiClient.post('/ssl-tools/generate-csr', values.keySize === 'ec' ? { ...values, keyType: 'ec', keySize: undefined } : values);
      const result = response.data;

      if (result.success) {
        setCsrResult(result.data);
        message.success(t('CSR generated'));
      } else {
        message.error(result.error || t('Failed to generate CSR'));
      }
    } catch (error) {
      // Don't show error for rate limit - captcha modal will handle it
      if (error instanceof RateLimitError || error?.isRateLimit ||
          error?.response?.status === 429 || error?.response?.status === 430) {
        console.log('[SslTools] Rate limit error caught, captcha modal should handle it');
        return;
      }
      console.error('CSR generation error:', error);
      message.error(t('Failed to generate CSR: {{error}}', { error: error.response?.data?.error || error.message }));
    } finally {
      setCSRLoading(false);
    }
  };

  // Complete certificate chain
  const completeCertificateChain = async (values) => {
    setChainLoading(true);
    try {
      const response = await apiClient.post('/ssl-tools/complete-chain', { certificate: values.certificate });
      const result = response.data;

      if (result.success) {
        setChainResult(result.data);
        message.success(t('Certificate chain completed'));
      } else {
        message.error(result.error || t('Failed to complete certificate chain'));
      }
    } catch (error) {
      if (error instanceof RateLimitError || error?.isRateLimit ||
          error?.response?.status === 429 || error?.response?.status === 430) {
        return;
      }
      console.error('Chain completion error:', error);
      message.error(t('Failed to complete certificate chain: {{error}}', { error: error.response?.data?.error || error.message }));
    } finally {
      setChainLoading(false);
    }
  };

  // Decode certificate
  const decodeCertificate = async (values) => {
    setDecoderLoading(true);
    try {
      const response = await apiClient.post('/ssl-tools/decode-cert', { certificate: values.certificate });
      const result = response.data;

      if (result.success) {
        setDecoderResult(result.data);
        message.success(t('Certificate parsed'));
      } else {
        message.error(result.error || t('Failed to parse certificate'));
      }
    } catch (error) {
      if (error instanceof RateLimitError || error?.isRateLimit ||
          error?.response?.status === 429 || error?.response?.status === 430) {
        return;
      }
      console.error('Certificate decode error:', error);
      message.error(t('Failed to parse certificate: {{error}}', { error: error.response?.data?.error || error.message }));
    } finally {
      setDecoderLoading(false);
    }
  };

  // Match key and certificate
  const matchKeyAndCert = async (values) => {
    setMatcherLoading(true);
    try {
      const response = await apiClient.post('/ssl-tools/match-key', {
        certificate: values.certificate,
        private_key: values.privateKey
      });
      const result = response.data;

      if (result.success) {
        setMatcherResult(result.data);
      } else {
        message.error(result.error || t('Verification failed'));
      }
    } catch (error) {
      if (error instanceof RateLimitError || error?.isRateLimit ||
          error?.response?.status === 429 || error?.response?.status === 430) {
        return;
      }
      console.error('Key matching error:', error);
      message.error(t('Verification failed: {{error}}', { error: error.response?.data?.error || error.message }));
    } finally {
      setMatcherLoading(false);
    }
  };

  const tabItems = [
    {
      key: 'csr',
      label: (
        <span>
          <KeyOutlined />
          {t('CSR generator')}
        </span>
      ),
      children: (
        <div>
          <Alert
            message={t("CSR (Certificate Signing Request) generator")}
            description={t("Generate a certificate signing request to apply for an SSL certificate from a certificate authority. Keep the generated private key secure and do not share it with others.")}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Row gutter={24}>
            <Col span={12}>
              <Card title={t("Enter information")} size="small">
                <Form
                  form={csrForm}
                  layout="vertical"
                  onFinish={generateCSR}
                  initialValues={{
                    keySize: '2048',
                    country: 'CN'
                  }}
                >
                  <Form.Item
                    name="commonName"
                    label={t("Domain (Common Name)")}
                    rules={[{ required: true, message: t('Please enter a domain') }]}
                  >
                    <Input placeholder={t("example.com or *.example.com")} />
                  </Form.Item>

                  <Form.Item
                    name="organization"
                    label={t("Organization/company name")}
                  >
                    <Input placeholder="Example Inc." />
                  </Form.Item>

                  <Form.Item
                    name="organizationalUnit"
                    label={t("Department")}
                  >
                    <Input placeholder="IT Department" />
                  </Form.Item>

                  <Form.Item
                    name="locality"
                    label={t("City")}
                  >
                    <Input placeholder="Beijing" />
                  </Form.Item>

                  <Form.Item
                    name="state"
                    label={t("Province/state")}
                  >
                    <Input placeholder="Beijing" />
                  </Form.Item>

                  <Form.Item
                    name="country"
                    label={t("Country code")}
                    rules={[{ len: 2, message: t('Please enter a two-letter country code') }]}
                  >
                    <Input placeholder="CN" maxLength={2} style={{ width: 100 }} prefix={<span style={{ display: 'inline-flex', width: 18 }}><CountryFlag code={csrCountry} style={{ marginInlineEnd: 0 }} /></span>} />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    label={t("Email")}
                  >
                    <Input placeholder="admin@example.com" />
                  </Form.Item>

                  <Form.Item
                    name="keySize"
                    label={t("Key")}
                  >
                    <Select style={{ width: 180 }}>
                      <Option value="2048">RSA 2048</Option>
                      <Option value="4096">RSA 4096</Option>
                      <Option value="ec">ECDSA P-256</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="san"
                    label={t("SAN domains (optional)")}
                    extra={t("Separate multiple domains with commas")}
                  >
                    <Input placeholder="www.example.com, api.example.com" />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={csrLoading} icon={<KeyOutlined />}>
                      {t('Generate CSR')}
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </Col>

            <Col span={12}>
              {csrResult && (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Card
                    title={t("CSR (Certificate Signing Request)")}
                    size="small"
                    extra={
                      <Space>
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(csrResult.csr, 'CSR')}
                        >
                          {t('Copy')}
                        </Button>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => downloadFile(csrResult.csr, `${csrForm.getFieldValue('commonName')}.csr`)}
                        >
                          {t('Download')}
                        </Button>
                      </Space>
                    }
                  >
                    <TextArea
                      value={csrResult.csr}
                      rows={10}
                      readOnly
                      style={{ fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </Card>

                  <Card
                    title={
                      <span>
                        {t('Private key')} <Tag color="red">{t('Please keep it secure')}</Tag>
                      </span>
                    }
                    size="small"
                    extra={
                      <Space>
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(csrResult.privateKey, t('Private key'))}
                        >
                          {t('Copy')}
                        </Button>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => downloadFile(csrResult.privateKey, `${csrForm.getFieldValue('commonName')}.key`)}
                        >
                          {t('Download')}
                        </Button>
                      </Space>
                    }
                  >
                    <TextArea
                      value={csrResult.privateKey}
                      rows={10}
                      readOnly
                      style={{ fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </Card>
                </Space>
              )}
            </Col>
          </Row>
        </div>
      )
    },
    {
      key: 'chain',
      label: (
        <span>
          <LinkOutlined />
          {t('Certificate chain completion')}
        </span>
      ),
      children: (
        <div>
          <Alert
            message={t("Certificate chain completion tool")}
            description={t("Automatically download and complete the intermediate certificate chain for your SSL certificate. If some browsers or devices do not trust your certificate, intermediate certificates may be missing.")}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Row gutter={24}>
            <Col span={12}>
              <Card title={t("Paste your certificate")} size="small">
                <Form
                  form={chainForm}
                  layout="vertical"
                  onFinish={completeCertificateChain}
                >
                  <Form.Item
                    name="certificate"
                    label={t("SSL certificates")}
                    rules={[{ required: true, message: t('Please paste your certificate') }]}
                  >
                    <TextArea
                      rows={15}
                      placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                      style={{ fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={chainLoading} icon={<LinkOutlined />}>
                      {t('Complete certificate chain')}
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </Col>

            <Col span={12}>
              {chainResult && (
                <Card
                  title={t("Full certificate chain")}
                  size="small"
                  extra={
                    <Space>
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(chainResult.fullChain, t('Certificate chain'))}
                      >
                        {t('Copy')}
                      </Button>
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => downloadFile(chainResult.fullChain, 'fullchain.crt')}
                      >
                        {t('Download')}
                      </Button>
                    </Space>
                  }
                >
                  <Alert
                    message={t('Found {{chainLength}} certificates', { chainLength: chainResult.chainLength })}
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  <TextArea
                    value={chainResult.fullChain}
                    rows={15}
                    readOnly
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                  {chainResult.certificates && (
                    <Collapse style={{ marginTop: 16 }}>
                      {chainResult.certificates.map((cert, index) => (
                        <Panel header={t('Certificate {{index}}: {{subject}}', { index: index + 1, subject: cert.subject })} key={index}>
                          <Descriptions size="small" column={1}>
                            <Descriptions.Item label={t("Issuer")}>{cert.issuer}</Descriptions.Item>
                            <Descriptions.Item label={t("Validity")}>{t('{{validFrom}} to {{validTo}}', { validFrom: cert.validFrom, validTo: cert.validTo })}</Descriptions.Item>
                          </Descriptions>
                        </Panel>
                      ))}
                    </Collapse>
                  )}
                </Card>
              )}
            </Col>
          </Row>
        </div>
      )
    },
    {
      key: 'decoder',
      label: (
        <span>
          <SafetyCertificateOutlined />
          {t('Certificate parsing')}
        </span>
      ),
      children: (
        <div>
          <Alert
            message={t("Certificate parser")}
            description={t("Parse an SSL certificate to view details such as domains, issuer, and validity period.")}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Row gutter={24}>
            <Col span={12}>
              <Card title={t("Paste certificate")} size="small">
                <Form
                  form={decoderForm}
                  layout="vertical"
                  onFinish={decodeCertificate}
                >
                  <Form.Item
                    name="certificate"
                    label={t("SSL certificates")}
                    rules={[{ required: true, message: t('Please paste the certificate') }]}
                  >
                    <TextArea
                      rows={15}
                      placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                      style={{ fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={decoderLoading} icon={<InfoCircleOutlined />}>
                      {t('Parse certificate')}
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </Col>

            <Col span={12}>
              {decoderResult && (
                <Card title={t("Certificate information")} size="small">
                  <Descriptions bordered column={1} size="small">
                    <Descriptions.Item label={t("Domain (CN)")}>
                      <Text strong>{decoderResult.commonName}</Text>
                    </Descriptions.Item>
                    {decoderResult.san && decoderResult.san.length > 0 && (
                      <Descriptions.Item label={t("SAN domains")}>
                        <Space wrap>
                          {decoderResult.san.map((domain, i) => (
                            <Tag key={i}>{domain}</Tag>
                          ))}
                        </Space>
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label={t("Issuer")}>
                      {decoderResult.issuer}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Organization")}>
                      {decoderResult.organization || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Valid from")}>
                      {decoderResult.validFrom}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Valid until")}>
                      <Space>
                        {decoderResult.validTo}
                        {decoderResult.isExpired ? (
                          <Tag color="red">{t('Expired')}</Tag>
                        ) : decoderResult.daysRemaining <= 30 ? (
                          <Tag color="orange">{t('Expiring soon ({{days}} days)', { days: decoderResult.daysRemaining })}</Tag>
                        ) : (
                          <Tag color="green">{t('Valid ({{days}} days)', { days: decoderResult.daysRemaining })}</Tag>
                        )}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Serial number")}>
                      <Text code style={{ fontSize: 11 }}>{decoderResult.serialNumber}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Signature algorithm")}>
                      {decoderResult.signatureAlgorithm}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Key type")}>
                      {t('{{keyType}} {{keySize}} bits', { keyType: decoderResult.keyType, keySize: decoderResult.keySize })}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              )}
            </Col>
          </Row>
        </div>
      )
    },
    {
      key: 'matcher',
      label: (
        <span>
          <CheckCircleOutlined />
          {t('Key matches')}
        </span>
      ),
      children: (
        <div>
          <Alert
            message={t("Key match verification")}
            description={t("Check whether a private key matches a certificate. Use this tool if you see a key mismatch error when installing a certificate.")}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form
            form={matcherForm}
            layout="vertical"
            onFinish={matchKeyAndCert}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="certificate"
                  label={t("SSL certificates")}
                  rules={[{ required: true, message: t('Please paste the certificate') }]}
                >
                  <TextArea
                    rows={12}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="privateKey"
                  label={t("Private key")}
                  rules={[{ required: true, message: t('Please paste the private key') }]}
                >
                  <TextArea
                    rows={12}
                    placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={matcherLoading} icon={<CheckCircleOutlined />}>
                {t('Verify match')}
              </Button>
            </Form.Item>
          </Form>

          {matcherResult && (
            <Alert
              message={matcherResult.matched ? t('Key matches') : t('Key does not match')}
              description={
                matcherResult.matched
                  ? t('The private key matches the certificate and can be used with it.')
                  : t('The private key does not match the certificate. Please check that you are using the correct private key.')
              }
              type={matcherResult.matched ? 'success' : 'error'}
              showIcon
              icon={matcherResult.matched ? <CheckCircleOutlined /> : <WarningOutlined />}
            />
          )}
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>
        <SafetyCertificateOutlined style={{ marginRight: 8 }} />
        {t('SSL tools')}
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        {t('Tools for CSR generation, certificate chain completion, certificate parsing, and key matching verification')}
      </Paragraph>

      <InlineCaptcha action="ssl_tools" />
      <Card>
        <Tabs items={tabItems} />
      </Card>
    </div>
  );
};

export default SslTools;
